/**
 * Shared types and formatting helpers for the Income Analyzer (Income
 * Summary, Residual Income, per-type Analysis, Add Income, Source
 * Verification). `INCOME_SOURCES` is the `isMockServices()` fallback fixture
 * (see getLoanIncomes.ts) -- everything else, including every screen this
 * file feeds, is real/server-backed. There's no Source Verification mock
 * data left here.
 */
import {
  Briefcase,
  Building2,
  CheckCircle2,
  CircleDollarSign,
  Clock,
  AlertCircle,
  FileText,
  Gift,
  Home,
  Landmark,
  LineChart,
  Percent,
  ShieldCheck,
  Sprout,
  Store,
  Users,
} from 'lucide-react';
export type IncomeSourceStatus = 'verified' | 'pending' | 'review';

/**
 * Which half of a borrower pair an income belongs to. loan_borrowers stores a
 * PAIR, so a single person is (borrowerPairId, scope) — the same convention
 * documents use.
 */
export type BorrowerScope = 'borrower' | 'coborrower';

/**
 * A DOCUMENT's scope, which is wider than an income stream's: a document can
 * also be tagged to both halves of a pair, or to the loan. Mirrors mos-mvp's
 * `DocumentScope` in infrastructure/tenantDatabase/models/documents.ts.
 */
export const DOCUMENT_SCOPE = {
  BORROWER: 'borrower',
  CO_BORROWER: 'coborrower',
  BOTH: 'both',
  LOAN: 'loan',
} as const;

/**
 * A document TYPE's scope. Only `borrower`-scoped types are ever attributed to
 * a person by the backend's document tagger — `common` (the column default)
 * and `pair` types keep a null borrower_pair_id forever. Mirrors mos-mvp's
 * `DocumentTypeScope` in infrastructure/tenantDatabase/models/documentTypes.ts;
 * note `common` is the value for what the UI calls loan-level.
 */
export const DOCUMENT_TYPE_SCOPE = {
  BORROWER: 'borrower',
  BORROWER_PAIR: 'pair',
  LOAN: 'common',
} as const;

export interface IncomeSource {
  id: string;
  type: string;
  employer: string;
  position: string;
  startDate: string;
  endDate?: string;
  payFrequency: string;
  monthly: number;
  ytd: number;
  /** Annual totals keyed by year, so no specific year is baked into the type. */
  years: Record<string, number>;
  status: IncomeSourceStatus;
  trend: number;
  /** Document type ids, sourced from the tenant's income grouping config. */
  supportingDocumentTypeIds: string[];
  borrowerPairId?: string | null;
  scope?: BorrowerScope | null;
  /** Every field the income type's form_json declares beyond the common ones
   *  above, flat, keyed by field name (e.g. `employmentSituation: string[]`
   *  for W-2). See mapLoanIncome.ts. */
  fields?: Record<string, unknown>;
  /** The analysis screen's user-entered table cells (e.g. earnings rows,
   *  Adjusted Months), keyed tableName -> rowKey -> colKey -> value, ABSOLUTE
   *  year (not offset) inside colKey where a table has year columns. Mirrors
   *  loan_incomes.analysis_json. Formula-computed cells are never stored here
   *  -- they recompute from form_json on load, so a formula fix reflows
   *  existing loans. */
  analysisTables?: Record<string, Record<string, Record<string, unknown>>>;
}

/**
 * The two completed years the Income Summary reports on, newest first.
 * Derived from the clock so the grid rolls over on its own each January.
 */
export const getIncomeYears = (now: Date = new Date()): [number, number] => {
  const year = now.getFullYear();
  return [year - 1, year - 2];
};

const [PRIOR_YEAR, PRIOR_YEAR_2] = getIncomeYears();

/** Mock-mode helper: builds the years map for the two reported years. */
const mockYears = (prior: number, prior2: number): Record<string, number> => ({
  [String(PRIOR_YEAR)]: prior,
  [String(PRIOR_YEAR_2)]: prior2,
});

const W2_SITUATION_FIELDS = {
  employmentSituation: [
    'currentEmployment',
    'recentPayRaise',
    'overtimeNotContinuing',
    'bonusNotContinuing',
  ],
};

export const INCOME_SOURCES: IncomeSource[] = [
  {
    id: 'i1',
    type: 'W-2 Employment Income',
    employer: 'DotGuard',
    position: 'Senior Data Engineer',
    startDate: 'Mar 2019',
    payFrequency: 'Monthly',
    monthly: 8_416.67,
    ytd: 84_166,
    years: mockYears(101_000, 94_200),
    status: 'verified',
    trend: 6.2,
    supportingDocumentTypeIds: ['dt-1040', 'dt-w2', 'dt-paystubs', 'dt-voe'],
    fields: W2_SITUATION_FIELDS,
  },
  {
    id: 'i5',
    type: 'W-2 Employment Income',
    employer: 'SkyLight',
    position: 'Software Engineer',
    startDate: 'Jun 2021',
    payFrequency: 'Monthly',
    monthly: 6_250.0,
    ytd: 62_500,
    years: mockYears(75_000, 71_500),
    status: 'verified',
    trend: 4.8,
    supportingDocumentTypeIds: ['dt-1040', 'dt-w2', 'dt-paystubs'],
    fields: W2_SITUATION_FIELDS,
  },
  {
    id: 'i8',
    type: '1099 Services Income',
    employer: 'Cutler Consulting',
    position: 'Independent Contractor',
    startDate: 'Feb 2022',
    payFrequency: 'Monthly',
    monthly: 3_750.0,
    ytd: 37_500,
    years: mockYears(45_000, 41_200),
    status: 'pending',
    trend: 9.2,
    supportingDocumentTypeIds: ['dt-1099-nec', 'dt-sched-c'],
  },
  {
    id: 'i4',
    type: 'Interests and Dividends',
    employer: 'Fidelity / Schwab',
    position: 'Investment Income',
    startDate: '2-yr avg',
    payFrequency: 'Monthly',
    monthly: 268.5,
    ytd: 3_222,
    years: mockYears(3_222, 2_980),
    status: 'pending',
    trend: 4.0,
    supportingDocumentTypeIds: ['dt-sched-b'],
  },
  {
    id: 'i9',
    type: 'Capital Gains and Losses',
    employer: 'Schwab Brokerage',
    position: 'Schedule D',
    startDate: '2-yr avg',
    payFrequency: 'Monthly',
    monthly: 412.0,
    ytd: 4_944,
    years: mockYears(4_944, 5_120),
    status: 'review',
    trend: -3.4,
    supportingDocumentTypeIds: ['dt-sched-d'],
  },
  {
    id: 'i3',
    type: 'Rental Real Estate',
    employer: '417 Linden Ave Duplex',
    position: 'Schedule E',
    startDate: 'Jan 2021',
    payFrequency: 'Monthly',
    monthly: 1_425.0,
    ytd: 17_100,
    years: mockYears(17_100, 17_460),
    status: 'review',
    trend: -2.1,
    supportingDocumentTypeIds: ['dt-sched-e', 'dt-lease'],
  },
  {
    id: 'i10',
    type: 'Supplementary Income (Estates & Trusts Beneficiary, Remics)',
    employer: 'Cutler Family Trust',
    position: 'Beneficiary',
    startDate: 'Jan 2023',
    payFrequency: 'Monthly',
    monthly: 540.0,
    ytd: 6_480,
    years: mockYears(6_480, 6_200),
    status: 'pending',
    trend: 4.5,
    supportingDocumentTypeIds: ['dt-sched-e'],
  },
  {
    id: 'i11',
    type: 'Farming Net Profits',
    employer: 'Green Acres Farm',
    position: 'Schedule F',
    startDate: 'Jan 2019',
    payFrequency: 'Monthly',
    monthly: 1_180.0,
    ytd: 14_160,
    years: mockYears(14_160, 13_400),
    status: 'pending',
    trend: 5.7,
    supportingDocumentTypeIds: ['dt-sched-f'],
  },
  {
    id: 'i6',
    type: 'Partnership Multi-Member (1065)',
    employer: 'MaybeKnots',
    position: 'Partner',
    startDate: 'Jan 2020',
    payFrequency: 'Monthly',
    monthly: 4_500.0,
    ytd: 54_000,
    years: mockYears(54_000, 52_100),
    status: 'verified',
    trend: 3.5,
    supportingDocumentTypeIds: ['dt-sched-e', 'dt-sched-k1', 'dt-1065'],
  },
  {
    id: 'i12',
    type: 'Sole Proprietorship Income (Schedule C)',
    employer: 'Cutler Design Studio',
    position: 'Owner',
    startDate: 'Apr 2020',
    payFrequency: 'Monthly',
    monthly: 2_900.0,
    ytd: 29_000,
    years: mockYears(34_800, 31_500),
    status: 'pending',
    trend: 10.5,
    supportingDocumentTypeIds: ['dt-sched-c'],
  },
  {
    id: 'i13',
    type: 'Shareholder Income S Corp (1120S)',
    employer: 'Northwind LLC',
    position: 'Shareholder',
    startDate: 'Jan 2021',
    payFrequency: 'Monthly',
    monthly: 3_250.0,
    ytd: 39_000,
    years: mockYears(39_000, 36_800),
    status: 'verified',
    trend: 6.0,
    supportingDocumentTypeIds: ['dt-sched-k1', 'dt-1120s'],
  },
  {
    id: 'i14',
    type: 'Shareholder Income C Corp (1120)',
    employer: 'Atlas Industries Inc',
    position: 'Shareholder',
    startDate: 'Jan 2022',
    payFrequency: 'Monthly',
    monthly: 1_800.0,
    ytd: 21_600,
    years: mockYears(21_600, 20_400),
    status: 'pending',
    trend: 5.9,
    supportingDocumentTypeIds: ['dt-1120'],
  },
  {
    id: 'i7',
    type: 'Military Pay',
    employer: 'US Army',
    position: 'Active Duty',
    startDate: 'May 2018',
    payFrequency: 'Monthly',
    monthly: 5_842.0,
    ytd: 58_420,
    years: mockYears(70_104, 67_980),
    status: 'verified',
    trend: 3.1,
    supportingDocumentTypeIds: ['dt-military-w2', 'dt-military-paystub'],
  },
  {
    id: 'i15',
    type: 'Pensions, Annuities and Social Security',
    employer: 'SSA / Vanguard Pension',
    position: 'Retirement Income',
    startDate: 'Jan 2023',
    payFrequency: 'Monthly',
    monthly: 2_240.0,
    ytd: 26_880,
    years: mockYears(26_880, 26_100),
    status: 'verified',
    trend: 3.0,
    supportingDocumentTypeIds: ['dt-ssa-1099', 'dt-1099-r'],
  },
  {
    id: 'i16',
    type: 'Other Income',
    employer: 'Misc Sources',
    position: 'Other',
    startDate: '2-yr avg',
    payFrequency: 'Monthly',
    monthly: 325.0,
    ytd: 3_900,
    years: mockYears(3_900, 3_750),
    status: 'review',
    trend: 4.0,
    supportingDocumentTypeIds: [],
  },
];

export const statusStyles: Record<
  IncomeSourceStatus,
  { label: string; cls: string; icon: typeof CheckCircle2 }
> = {
  verified: {
    label: 'Verified',
    cls: 'bg-success/10 text-success border border-success/20',
    icon: CheckCircle2,
  },
  pending: {
    label: 'Pending Docs',
    cls: 'bg-warning/15 text-warning-foreground border border-warning/30',
    icon: Clock,
  },
  review: {
    label: 'Needs Review',
    cls: 'bg-destructive/10 text-destructive border border-destructive/20',
    icon: AlertCircle,
  },
};

/* Maps an income status onto a variant of the themed <Badge> (src/components/Badge). */
export const STATUS_BADGE_VARIANT: Record<
  IncomeSourceStatus,
  'success' | 'warning' | 'danger'
> = {
  verified: 'success',
  pending: 'warning',
  review: 'danger',
};

export const INCOME_TYPE_ICONS: Record<string, typeof Briefcase> = {
  'W-2 Employment Income': Briefcase,
  '1099 Services Income': FileText,
  'Interests and Dividends': Percent,
  'Capital Gains and Losses': LineChart,
  'Rental Real Estate': Home,
  'Supplementary Income (Estates & Trusts Beneficiary, Remics)': Gift,
  'Farming Net Profits': Sprout,
  'Partnership Multi-Member (1065)': Users,
  'Sole Proprietorship Income (Schedule C)': Store,
  'Shareholder Income S Corp (1120S)': Building2,
  'Shareholder Income C Corp (1120)': Building2,
  'Military Pay': ShieldCheck,
  'Pensions, Annuities and Social Security': Landmark,
  'Other Income': CircleDollarSign,
};

export const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

// VA residual income table (Northeast region, loan >= $80k) — simplified lookup.
export const RESIDUAL_REQUIRED: Record<number, number> = {
  1: 441,
  2: 738,
  3: 889,
  4: 1003,
  5: 1039,
};

/* ------------------------------------------------------------- formatters */

export const fmt = (n: number) =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

export const fmt2 = (n: number) =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });

/* AG Grid theme params (passed to the shared <AGGrid themeParams=...>). */
export const incomeGridThemeParams = {
  headerBackgroundColor: '#f1f5f9',
  headerTextColor: '#64748b',
  oddRowBackgroundColor: '#f8fafc',
  rowHoverColor: 'rgba(3, 138, 255, 0.05)',
  fontSize: 14,
  headerFontSize: 13,
  headerFontWeight: 600,
  rowHeight: 42,
  headerHeight: 42,
  spacing: 8,
};
